import React from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { TableCell, TableRow } from "@shadcn-in-nx/ui/table";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shadcn-in-nx/ui/tooltip";
import { Edit, Trash2, Eye, Ban, CheckCircle2, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString();
}

function formatConfigValue(value, fieldConfig, fieldKey) {
    if (value === undefined || value === null || value === "") return "-";
    if (fieldConfig.type === "boolean") return value ? "Yes" : "No";
    if (fieldConfig.format === "date" || fieldConfig.type === "date") {
        return new Date(value).toLocaleDateString();
    }
    if (fieldConfig.format === "date-time" || fieldConfig.type === "date-time" ||
        fieldConfig.type === "datetime" || /_at$/i.test(fieldKey)) {
        return new Date(value).toLocaleString();
    }
    if (fieldConfig.type === "array" && Array.isArray(value)) {
        return value.length > 0 ? value.join(", ") : "-";
    }
    if (fieldConfig.type === "object" && typeof value === "object") {
        const keys = Object.keys(value);
        return keys.length > 0 ? keys.map((k) => `${k}: ${value[k]}`).join(", ") : "-";
    }
    return String(value);
}

function UserTableRow({ user, configKeys, getFieldConfig, viewAsUserId, onEdit, onDelete, onViewAs, onToggleBlock }) {
    const { t } = useTranslation();

    return (
        <TableRow>
            <TableCell className="font-medium text-sm max-w-[140px] truncate">{user.username}</TableCell>
            <TableCell className="hidden sm:table-cell">
                <span className="truncate block max-w-[180px] text-sm">{user.email}</span>
            </TableCell>
            <TableCell className="hidden lg:table-cell text-sm">{user.firstName} {user.lastName}</TableCell>
            <TableCell>
                <Badge variant="outline" className="text-xs">
                    {user.roles?.[0] || "-"}
                </Badge>
            </TableCell>
            <TableCell className="hidden sm:table-cell">
                <Badge variant={user.isActive ? "default" : "destructive"} className="text-xs">
                    {user.isActive ? t("workflowUsers.active") : t("workflowUsers.userIsBlocked")}
                </Badge>
            </TableCell>
            <TableCell className="hidden xl:table-cell">
                <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(user.createdAt)}
                </div>
            </TableCell>
            {configKeys.map((key) => (
                <TableCell key={key} className="hidden xl:table-cell text-sm">
                    {formatConfigValue(user.config?.[key], getFieldConfig(key), key)}
                </TableCell>
            ))}
            <TableCell className="text-right p-1">
                <div className="flex items-center justify-end">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className={`h-7 w-7 ${viewAsUserId === user.id ? "text-amber-600" : ""}`} onClick={() => onViewAs(user)} disabled={!user.isActive}>
                                <Eye className="w-3.5 h-3.5" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {user.isActive ? t("workflowUsers.viewAppAs", { username: user.username }) : t("workflowUsers.userIsBlocked")}
                        </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className={`h-7 w-7 ${!user.isActive ? "text-green-600" : "text-red-600"}`} onClick={() => onToggleBlock(user)}>
                                {user.isActive ? <Ban className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {user.isActive ? t("workflowUsers.blockUser") : t("workflowUsers.unblockUser")}
                        </TooltipContent>
                    </Tooltip>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(user)}>
                        <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => onDelete(user)}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
}

export default React.memo(UserTableRow);
